/** Shared clipboard / print helpers for Early Signals table actions. */

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function buildHtmlTable(headers: string[], rows: string[][]): string {
  const th = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('')
  const body = rows
    .map((row) => `<tr>${row.map((c) => `<td>${escapeHtml(String(c ?? ''))}</td>`).join('')}</tr>`)
    .join('')
  return `<table><thead><tr>${th}</tr></thead><tbody>${body}</tbody></table>`
}

export interface CopyTableToClipboardOptions {
  headers: string[]
  rows: string[][]
  /** Optional plain-text lines above the table (title, meta). */
  preamble?: string[]
}

/** Preamble paragraphs + table markup (shared by clipboard and print). */
export function buildTableBodyHtml(options: CopyTableToClipboardOptions): string {
  const { headers, rows, preamble } = options
  const tableHtml = buildHtmlTable(headers, rows)
  const lines = (preamble ?? []).filter((line) => line != null && String(line).trim() !== '')
  if (lines.length === 0) return tableHtml
  return `${lines.map((line) => `<p>${escapeHtml(String(line))}</p>`).join('')}${tableHtml}`
}

const PRINT_DOCUMENT_STYLES = `
  @page { margin: 16mm; }
  body { font-family: system-ui, -apple-system, Segoe UI, sans-serif; font-size: 11pt; color: #111; margin: 0; padding: 8mm; }
  p { margin: 0 0 0.4em; line-height: 1.35; }
  table { width: 100%; border-collapse: collapse; margin-top: 0.75em; }
  th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; font-size: 10pt; }
  td { font-size: 10pt; }
`

/** Full HTML document used by the print iframe (and reusable for tooling). */
export function buildPrintDocumentHtml(options: CopyTableToClipboardOptions): string {
  const bodyHtml = buildTableBodyHtml(options)
  return `<!DOCTYPE html><html><head><meta charset="utf-8" />
<title>Print</title>
<style>${PRINT_DOCUMENT_STYLES}</style></head><body>${bodyHtml}</body></html>`
}

export async function copyTableToClipboard(options: CopyTableToClipboardOptions): Promise<void> {
  const { headers, rows, preamble } = options
  const tableTsv = [headers, ...rows.map((r) => r.map((c) => String(c ?? '')))].map((r) => r.join('\t')).join('\n')
  const preambleText = (preamble ?? []).filter((line) => line != null && String(line).trim() !== '').join('\n')
  const tsv = preambleText ? `${preambleText}\n\n${tableTsv}` : tableTsv
  const html = buildTableBodyHtml(options)

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([tsv], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ])
      return
    } catch {
      // fall through
    }
  }
  await navigator.clipboard.writeText(tsv)
}

/**
 * Open the browser print dialog with a clean printable table for the payload.
 */
export function printTablePayload(options: CopyTableToClipboardOptions): void {
  if (typeof document === 'undefined') return

  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  document.body.appendChild(iframe)

  const doc = iframe.contentDocument || iframe.contentWindow?.document
  if (!doc) {
    iframe.remove()
    return
  }

  doc.open()
  doc.write(buildPrintDocumentHtml(options))
  doc.close()

  const cleanup = () => {
    window.setTimeout(() => {
      iframe.remove()
    }, 500)
  }

  const win = iframe.contentWindow
  if (!win) {
    cleanup()
    return
  }

  win.focus()
  win.addEventListener('afterprint', cleanup, { once: true })
  // Fallback cleanup if afterprint is skipped
  window.setTimeout(cleanup, 60_000)

  // Allow layout before print
  window.setTimeout(() => {
    try {
      win.print()
    } catch {
      cleanup()
    }
  }, 50)
}

/** Cell helper: blank → em dash for paste readability. */
export function cell(value: string | number | null | undefined): string {
  if (value == null) return '—'
  const s = String(value).trim()
  return s || '—'
}
